import * as React from 'react'
import { useApiClient } from 'hooks'
import { AxiosInstance, AxiosResponse } from 'axios'
import { ApiErrorDto } from '../api'
import { EL600 } from 'cs-kevlar-react/lib/es/theming/palette'

interface ErrorBoundaryState {
  hasError: boolean
}

interface ErrorBoundaryProps {
  children: any
  apiClient: AxiosInstance
}

const buildErrorMessage = (error: any) => {
  if (!error) return ''

  if (typeof error === 'string') return error

  if (error.message && error.stack) {
    return `${error.message}\n${error.stack}`
  } else if (error.message) {
    return error.message
  }

  console.log('unknown error', error)
  return ''
}

// there is no functional component equivalent for `componentDidCatch`
// so we have to use a class component
class ErrorBoundaryComponent extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: any, errorInfo: any) {
    try {
      const data: ApiErrorDto = {
        message: buildErrorMessage(error),
        stackInfo: errorInfo?.componentStack,
        url: window.location.href,
      }

      this.props.apiClient.request<void, AxiosResponse<void>>({
        url: `api/error`,
        method: 'post',
        withCredentials: true,
        data,
      })
    } catch (e) {
      console.error(e, 'Failed to post error to the server')
    }

    console.error(error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <React.Fragment>
          <h1 style={{ color: EL600 }}>Error.</h1>
          <h2>An error occurred while processing your request.</h2>
        </React.Fragment>
      )
    }

    return this.props.children
  }
}

export const ErrorBoundary = (props: any) => {
  const apiClient = useApiClient()
  return <ErrorBoundaryComponent apiClient={apiClient} {...props} />
}
